// Schedule: the master calendar plus the generator.
//
// Everything is editable: dates, courses, which nine, first tee time,
// interval, number of tee times, which divisions play where, and which two
// divisions share tee times each week.
//
// Regenerating replaces all regular-season matches. Any results already
// entered are lost, so the button says so and asks for confirmation.

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Calendar, Plus, Trash2, Wand2, AlertTriangle, CheckCircle2,
  Shuffle, Save, RotateCcw,
} from 'lucide-react';
import DivisionBadge from './DivisionBadge';
import ReadOnlyNotice from './ReadOnlyNotice';
import { DIVISIONS, NINES, PLAYERS_PER_DIVISION, REGULAR_SEASON_ROUNDS } from '../utils/constants';
import { formatDate, formatTime, teeTimeSlots, uid } from '../utils/helpers';
import { checkDateCapacity, allDivisionPairings, rotatePairings, validateRoundRobin, roundRobin } from '../utils/scheduling';

export default function ScheduleTab({
  seasonDates, courses, golfers, matches, isAdmin,
  onSaveDate, onDeleteDate, onSaveAssignment, onDeleteAssignment,
  onSavePairings, onGenerateSeason, onClearSeason,
}) {
  const [busy, setBusy] = useState(false);

  const active = useMemo(() => (golfers || []).filter(g => g.active !== false), [golfers]);
  const byDivision = useMemo(() => {
    const m = {};
    DIVISIONS.forEach(d => { m[d] = active.filter(g => g.division === d).map(g => g.id); });
    return m;
  }, [active]);

  const regular = useMemo(
    () => (seasonDates || []).filter(d => (d.phase || 'regular') === 'regular').sort((a, b) => a.seq - b.seq),
    [seasonDates]);
  const playoff = useMemo(
    () => (seasonDates || []).filter(d => d.phase === 'playoff').sort((a, b) => a.seq - b.seq),
    [seasonDates]);

  const courseName = (id) => (courses || []).find(c => c.id === id)?.name || id;

  // ---- Pre-flight checks -------------------------------------------------
  const problems = useMemo(() => {
    const out = [];
    DIVISIONS.forEach(d => {
      const n = byDivision[d].length;
      if (n !== PLAYERS_PER_DIVISION) {
        out.push({ level: 'error', text: `${d} has ${n} players. A ${REGULAR_SEASON_ROUNDS}-round round robin needs exactly ${PLAYERS_PER_DIVISION}.` });
      }
    });
    if (regular.length !== REGULAR_SEASON_ROUNDS) {
      out.push({
        level: regular.length < REGULAR_SEASON_ROUNDS ? 'warn' : 'error',
        text: `${regular.length} regular season dates configured. The round robin produces exactly ${REGULAR_SEASON_ROUNDS} rounds` +
              (regular.length < REGULAR_SEASON_ROUNDS
                ? ' - some players will not face everyone in their division.'
                : ' - extra dates will have no matches.'),
      });
    }
    regular.forEach(d => {
      checkDateCapacity(d, byDivision).forEach(text =>
        out.push({ level: 'error', text: `${d.label || d.play_date}: ${text}` }));
      if (!d.pairings || d.pairings.length === 0) {
        out.push({ level: 'error', text: `${d.label || d.play_date}: no division pairings set.` });
      }
      if (d.split_courses) {
        const wc = (d.assignments || []).find(a => (a.divisions || []).includes('Woods'))?.course_id;
        const pc = (d.assignments || []).find(a => (a.divisions || []).includes('Palmer'))?.course_id;
        if (wc && pc && wc === pc) {
          out.push({ level: 'error', text: `${d.label || d.play_date}: Woods and Palmer are both at ${courseName(wc)}. They must be at different courses on split weeks.` });
        }
      }
    });
    DIVISIONS.forEach(d => {
      if (byDivision[d].length === PLAYERS_PER_DIVISION) {
        const v = validateRoundRobin(byDivision[d], roundRobin(byDivision[d]));
        if (!v.valid) out.push({ level: 'error', text: `${d} round robin invalid: ${v.errors[0]}` });
      }
    });
    return out;
  }, [regular, byDivision, courses]); // eslint-disable-line react-hooks/exhaustive-deps

  const blocking = problems.filter(p => p.level === 'error');
  const generatedCount = (matches || []).filter(m => (m.phase || 'regular') === 'regular').length;

  const setPairingOption = async (date, optionIndex) => {
    const options = allDivisionPairings(DIVISIONS);
    await onSavePairings(date, options[optionIndex]);
  };


  return (
    <div className="mt-4 space-y-6">
      {!isAdmin && <ReadOnlyNotice>Schedule is view-only. Unlock admin to edit or regenerate.</ReadOnlyNotice>}

      {/* ---- Generator ---- */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5" />Generate the regular season</CardTitle>
            <CardDescription>
              Builds a {REGULAR_SEASON_ROUNDS}-round round robin inside each division - every player faces
              all {PLAYERS_PER_DIVISION - 1} division rivals exactly once. Each foursome is two players from
              one division and two from another, so each tee time hosts two head-to-head matches.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {problems.length > 0 && (
              <div className="space-y-1">
                {problems.map((p, i) => (
                  <div key={i} className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
                    p.level === 'error'
                      ? 'border-red-200 bg-red-50 text-red-900'
                      : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{p.text}</span>
                  </div>
                ))}
              </div>
            )}
            {problems.length === 0 && (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900">
                <CheckCircle2 className="h-4 w-4" />
                All checks pass. {regular.length} dates &times; 8 tee times = {regular.length * 8} matches.
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={blocking.length > 0 || busy}
                onClick={async () => {
                  const msg = generatedCount > 0
                    ? `This replaces all ${generatedCount} existing regular-season matches, INCLUDING any results already entered. Continue?`
                    : 'Generate the regular season schedule?';
                  if (!window.confirm(msg)) return;
                  setBusy(true);
                  try { await onGenerateSeason(); } finally { setBusy(false); }
                }}
              >
                <Wand2 className="mr-2 h-4 w-4" />
                {busy ? 'Generating...' : 'Generate regular season'}
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  if (!window.confirm('Reset division pairings to a rotating default? Woods and Palmer stay at different courses on split weeks.')) return;
                  setBusy(true);
                  try {
                    const rotated = rotatePairings(regular, DIVISIONS, ['Woods', 'Palmer']);
                    for (const d of rotated) await onSavePairings(d, d.pairings);
                  } finally { setBusy(false); }
                }}
              >
                <Shuffle className="mr-2 h-4 w-4" /> Reset rotating pairings
              </Button>
              {generatedCount > 0 && (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={async () => {
                    if (!window.confirm(`Delete all ${generatedCount} regular-season matches and their results?`)) return;
                    setBusy(true);
                    try { await onClearSeason(); } finally { setBusy(false); }
                  }}
                >
                  <RotateCcw className="mr-2 h-4 w-4" /> Clear generated matches
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- Regular season dates ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Regular season</CardTitle>
          <CardDescription>{regular.length} dates. Edit anything here, then regenerate.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {regular.map((date, idx) => (
            <DateEditor
              key={date.id}
              date={date}
              roundNumber={idx + 1}
              courses={courses}
              isAdmin={isAdmin}
              matches={matches}
              onSaveDate={onSaveDate}
              onDeleteDate={onDeleteDate}
              onSaveAssignment={onSaveAssignment}
              onDeleteAssignment={onDeleteAssignment}
              pairingSlot={
                <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <Label className="text-xs">Division pairings (who shares tee times)</Label>
                    {isAdmin && (
                      <div className="flex gap-1">
                        {allDivisionPairings(DIVISIONS).map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => setPairingOption(date, i)}
                            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] hover:bg-gray-100"
                            title={opt.map(p => p.join('+')).join(' | ')}
                          >
                            {opt.map(p => p.map(d => d[0]).join('')).join('/')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    {(date.pairings || []).length === 0
                      ? <span className="text-red-600">not set</span>
                      : (date.pairings || []).map((p, i) => (
                        <span key={i} className="flex items-center gap-1">
                          <DivisionBadge division={p[0]} />
                          <span className="text-gray-400">+</span>
                          <DivisionBadge division={p[1]} />
                        </span>
                      ))}
                  </div>
                </div>
              }
            />
          ))}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => onSaveDate({
                id: uid('sd'),
                seq: (Math.max(0, ...(seasonDates || []).filter(d => d.phase === 'regular').map(d => d.seq)) + 1),
                play_date: new Date().toISOString().slice(0, 10),
                label: `Week ${regular.length + 1}`,
                phase: 'regular',
                split_courses: false,
              })}
            >
              <Plus className="mr-1 h-4 w-4" /> Add a date
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ---- Playoff dates ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Playoffs</CardTitle>
          <CardDescription>
            Tee times and courses for each bracket round. Matchups are set on the Playoffs tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {playoff.map(date => (
            <DateEditor
              key={date.id}
              date={date}
              courses={courses}
              isAdmin={isAdmin}
              matches={matches}
              showBracketRound
              onSaveDate={onSaveDate}
              onDeleteDate={onDeleteDate}
              onSaveAssignment={onSaveAssignment}
              onDeleteAssignment={onDeleteAssignment}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------
// One editable date, with its course/nine/tee-time assignments
// -----------------------------------------------------------------------
const BRACKET_NAMES = { 1: 'Round of 16', 2: 'Quarterfinals', 3: 'Semifinals', 4: 'Championship' };

function DateEditor({
  date, roundNumber, courses, isAdmin, matches, pairingSlot, showBracketRound,
  onSaveDate, onDeleteDate, onSaveAssignment, onDeleteAssignment,
}) {
  const [draft, setDraft] = useState({});
  const [asgDrafts, setAsgDrafts] = useState({});

  const m = { ...date, ...draft };
  const dirty = Object.keys(draft).length > 0;
  const matchCount = (matches || []).filter(x => x.season_date_id === date.id).length;

  const saveDate = async () => {
    const payload = {
      id: date.id, seq: Number(m.seq), play_date: m.play_date, label: m.label,
      phase: m.phase, split_courses: !!m.split_courses, notes: m.notes || null,
    };
    await onSaveDate(payload);
    setDraft({});
  };

  const asgMerged = (a) => ({ ...a, ...(asgDrafts[a.id] || {}) });
  const asgDirty = (a) => !!asgDrafts[a.id] && Object.keys(asgDrafts[a.id]).length > 0;
  const editAsg = (id, k, v) => setAsgDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), [k]: v } }));

  const saveAsg = async (a) => {
    const x = asgMerged(a);
    await onSaveAssignment({
      id: a.id,
      season_date_id: date.id,
      course_id: x.course_id,
      nine: x.nine || 'F9',
      divisions: x.divisions || [],
      first_tee_time: x.first_tee_time,
      interval_min: Number(x.interval_min || 10),
      slot_count: Number(x.slot_count || 1),
      bracket_round: x.bracket_round ? Number(x.bracket_round) : null,
    });
    setAsgDrafts(d => { const c = { ...d }; delete c[a.id]; return c; });
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap items-end gap-2">
          {roundNumber && (
            <div className="mr-1 flex h-10 w-10 items-center justify-center rounded-md bg-gray-900 text-sm font-bold text-white"
                 title={`Round-robin round ${roundNumber}`}>
              R{roundNumber}
            </div>
          )}
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" className="h-9 w-40" disabled={!isAdmin}
              value={m.play_date || ''} onChange={e => setDraft(d => ({ ...d, play_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input className="h-9 w-56" disabled={!isAdmin}
              value={m.label || ''} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
          </div>
          <div className="pb-1 text-xs text-gray-500">
            {formatDate(m.play_date)} &middot; {matchCount} match{matchCount === 1 ? '' : 'es'} generated
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={!!m.split_courses}
                onChange={e => setDraft(d => ({ ...d, split_courses: e.target.checked }))} />
              split courses
            </label>
            {dirty && <span className="text-xs text-amber-600">unsaved</span>}
            <Button size="sm" disabled={!dirty} onClick={saveDate}><Save className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost"
              onClick={() => {
                if (window.confirm(`Delete ${m.label || m.play_date} and its ${matchCount} matches?`)) onDeleteDate(date.id);
              }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {pairingSlot}

      <div className="mt-3 space-y-2">
        {(date.assignments || []).map(a => {
          const x = asgMerged(a);
          const slots = teeTimeSlots(x.first_tee_time || '00:00', Number(x.interval_min || 10), Number(x.slot_count || 1));
          return (
            <div key={a.id} className="rounded-md border border-gray-200 bg-white p-2">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                <div>
                  <Label className="text-xs">Course</Label>
                  <select className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                    disabled={!isAdmin} value={x.course_id || ''}
                    onChange={e => editAsg(a.id, 'course_id', e.target.value)}>
                    {(courses || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Nine</Label>
                  <select className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                    disabled={!isAdmin} value={x.nine || 'F9'}
                    onChange={e => editAsg(a.id, 'nine', e.target.value)}>
                    {NINES.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">1st tee</Label>
                  <Input type="time" className="h-9" disabled={!isAdmin} value={x.first_tee_time || ''}
                    onChange={e => editAsg(a.id, 'first_tee_time', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Interval</Label>
                  <Input type="number" className="h-9" disabled={!isAdmin} value={x.interval_min ?? 10}
                    onChange={e => editAsg(a.id, 'interval_min', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Tee times</Label>
                  <Input type="number" className="h-9" disabled={!isAdmin} value={x.slot_count ?? 1}
                    onChange={e => editAsg(a.id, 'slot_count', e.target.value)} />
                </div>
                <div className="flex items-end gap-1">
                  {isAdmin && (
                    <>
                      <Button size="sm" disabled={!asgDirty(a)} onClick={() => saveAsg(a)}>
                        <Save className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDeleteAssignment(a.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {showBracketRound ? (
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-xs">Bracket round</Label>
                  <select className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                    disabled={!isAdmin} value={x.bracket_round || ''}
                    onChange={e => editAsg(a.id, 'bracket_round', e.target.value)}>
                    <option value="">(none)</option>
                    {[1, 2, 3, 4].map(r => <option key={r} value={r}>{BRACKET_NAMES[r]}</option>)}
                  </select>
                </div>
              ) : (
                <div className="mt-2">
                  <Label className="text-xs">Divisions at this course</Label>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {DIVISIONS.map(d => {
                      const on = (x.divisions || []).includes(d);
                      return (
                        <label key={d} className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            disabled={!isAdmin}
                            checked={on}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...(x.divisions || []), d]
                                : (x.divisions || []).filter(v => v !== d);
                              editAsg(a.id, 'divisions', next);
                            }}
                          />
                          <DivisionBadge division={d} />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="mt-2 text-xs text-gray-500">
                Tee times: {slots.map(formatTime).join(', ')}
              </p>
            </div>
          );
        })}

        {isAdmin && (
          <Button
            variant="outline" size="sm"
            onClick={() => onSaveAssignment({
              id: uid('asg'),
              season_date_id: date.id,
              course_id: courses?.[0]?.id,
              nine: 'F9',
              divisions: date.phase === 'playoff' ? [] : [...DIVISIONS],
              first_tee_time: '16:30',
              interval_min: 10,
              slot_count: 8,
            })}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add a course to this date
          </Button>
        )}
      </div>
    </div>
  );
}
